import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Badge, Modal, Spin, Alert, Space, Tooltip, Typography, Button, Divider } from 'antd';
import { ArrowRightOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import apiStatus from '@api/apiStatus';
import './StatusIndicator.scss';
import { OpenLink, TelegramLogo } from '@assets/icons/ui';
import { useMedia } from '@hooks/useMedia';
import { STATUS_CONFIGS } from '@shared/constants/servicesStatus.constants';
import { formatDate, formatTime } from '@utils/helpers/dateHelpers';

const { Text } = Typography;

const TOOLTIP_SERVICE_LIMIT = 3;
const STATUS_REQUEST_TIMEOUT_MS = 15000;
const SUPPORTED_INCIDENT_STYLES = new Set(['', 'info', 'success', 'warning', 'danger']);

const normalizeOptionalText = (value, fieldName) => {
  if (value === null || value === undefined) return '';
  if (typeof value !== 'string') {
    throw new Error(`Invalid service status ${fieldName}`);
  }
  return value.trim();
};

const normalizeOptionalDate = (value) => {
  if (!value) return null;
  return Number.isNaN(new Date(value).getTime()) ? null : value;
};

const normalizeStatusResponse = (payload) => {
  const data = Array.isArray(payload) && payload.length === 1 ? payload[0] : payload;

  if (!data || typeof data !== 'object' || Array.isArray(data) || typeof data.healthy !== 'boolean') {
    throw new Error('Invalid service status response');
  }

  if (data.incident !== null && data.incident !== undefined &&
    (typeof data.incident !== 'object' || Array.isArray(data.incident))) {
    throw new Error('Invalid service status incident');
  }

  const rawMonitorsDown = data.monitors_down ?? [];
  if (!Array.isArray(rawMonitorsDown)) {
    throw new Error('Invalid service status monitors');
  }

  const incidentStyle = data.incident
    ? normalizeOptionalText(data.incident.style, 'incident style').toLowerCase()
    : '';

  if (!SUPPORTED_INCIDENT_STYLES.has(incidentStyle)) {
    throw new Error('Invalid service status incident style');
  }

  const incident = data.incident ? {
    ...data.incident,
    style: incidentStyle,
    title: normalizeOptionalText(data.incident.title, 'incident title'),
    content: normalizeOptionalText(data.incident.content, 'incident content'),
    createdDate: normalizeOptionalDate(data.incident.createdDate),
    lastUpdatedDate: normalizeOptionalDate(data.incident.lastUpdatedDate),
  } : null;

  const monitorsDown = rawMonitorsDown.map((monitor) => {
    if (!monitor || typeof monitor !== 'object' || Array.isArray(monitor)) {
      throw new Error('Invalid affected service');
    }

    const title = normalizeOptionalText(monitor.title, 'monitor title');
    if (!title) {
      throw new Error('Affected service title is missing');
    }

    return {
      ...monitor,
      title,
      content: normalizeOptionalText(monitor.content, 'monitor content'),
    };
  });

  return {
    ...data,
    incident,
    monitors_down: monitorsDown,
  };
};

const handleKeyboardAction = (event, action) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
};

const getStatusText = (t, status) => {
  switch (status) {
  case 'unavailable':
    return t('title.statusUnavailable');
  case 'loading':
    return t('title.loading');
  case 'operational':
    return t('title.allSystemsOperational');
  case 'critical':
    return t('title.serviceDegradation');
  case 'partial':
    return t('title.partialServiceDegradation');
  default:
    return t('title.statusUnavailable');
  }
};

const StatusIndicator = () => {
  const { t } = useTranslation();
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const requestInFlightRef = useRef(false);
  const activeRequestControllerRef = useRef(null);
  const isMountedRef = useRef(false);
  const { isDesktop } = useMedia();

  const fetchStatus = useCallback(async () => {
    if (requestInFlightRef.current) return;

    const controller = new AbortController();
    let requestTimedOut = false;
    let timeoutId;

    requestInFlightRef.current = true;
    activeRequestControllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const abortedRequest = new Promise((_, reject) => {
        controller.signal.addEventListener('abort', () => {
          reject(new Error(
            requestTimedOut
              ? 'Service status request timed out'
              : 'Service status request cancelled'
          ));
        }, { once: true });
      });
      timeoutId = setTimeout(() => {
        requestTimedOut = true;
        controller.abort();
      }, STATUS_REQUEST_TIMEOUT_MS);

      const statusRequest = apiStatus.getServicesStatus({ signal: controller.signal });
      const { data } = await Promise.race([statusRequest, abortedRequest]);

      if (!isMountedRef.current || activeRequestControllerRef.current !== controller) return;
      setStatusData(normalizeStatusResponse(data));
      setLastUpdated(new Date());
    } catch (err) {
      if (!isMountedRef.current || activeRequestControllerRef.current !== controller ||
        (controller.signal.aborted && !requestTimedOut)) return;
      console.error('Failed to fetch status:', err);
      setError(true);
    } finally {
      clearTimeout(timeoutId);
      if (activeRequestControllerRef.current === controller) {
        activeRequestControllerRef.current = null;
        requestInFlightRef.current = false;
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    }
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    fetchStatus();
    const interval = setInterval(() => {
      fetchStatus();
    }, 600000);

    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
      const activeController = activeRequestControllerRef.current;
      activeRequestControllerRef.current = null;
      requestInFlightRef.current = false;
      activeController?.abort();
    };
  }, [fetchStatus]);

  const status = useMemo(() => {
    if (!statusData) {
      return error ? STATUS_CONFIGS.unavailable : STATUS_CONFIGS.loading;
    }

    const { healthy, incident } = statusData;
    const incidentStyle = incident?.style;
    const hasMonitorsDown = statusData.monitors_down.length > 0;

    if (incidentStyle === 'danger') {
      return STATUS_CONFIGS.critical;
    }

    if (incidentStyle === 'warning' || !healthy || hasMonitorsDown) {
      return STATUS_CONFIGS.partial;
    }

    return STATUS_CONFIGS.operational;
  }, [statusData, error]);
  const statusText = getStatusText(t, status.status);
  const monitorsDown = statusData?.monitors_down ?? [];
  const incident = statusData?.incident;
  const incidentHeading = t(
    incident && statusData.healthy && !['danger', 'warning'].includes(incident.style)
      ? 'title.serviceNotice'
      : 'title.activeIncidents'
  );
  const hasIncidentDetails = Boolean(
    incident && (
      incident.title || incident.content || incident.createdDate || incident.lastUpdatedDate
    )
  );
  const hasIncidentDescription = Boolean(
    incident && (incident.content || incident.createdDate || incident.lastUpdatedDate)
  );
  const degradationDetailsUnavailable = Boolean(
    statusData && ['critical', 'partial'].includes(status.status) &&
    !hasIncidentDetails && monitorsDown.length === 0
  );
  const statusErrorMessage = error
    ? t(statusData ? 'description.statusRefreshFailure' : 'description.statusLoadFailure')
    : '';
  const statusAccessibleSummary = useMemo(() => {
    const summary = [statusText];
    const incidentSummary = incident?.title || incident?.content;

    if (statusErrorMessage) {
      summary.push(statusErrorMessage);
    }
    if (degradationDetailsUnavailable) {
      summary.push(t('description.statusDetailsUnavailable'));
    }
    if (incidentSummary) {
      summary.push(`${incidentHeading}: ${incidentSummary}`);
    }
    if (monitorsDown.length > 0) {
      summary.push(
        `${t('title.affectedServices', { count: monitorsDown.length })}: ${monitorsDown
          .map((monitor) => monitor.title)
          .join(', ')}`
      );
    }

    return summary.join('. ');
  }, [degradationDetailsUnavailable, incident, incidentHeading, monitorsDown, statusErrorMessage, statusText, t]);

  const statusTooltipContent = useMemo(() => {
    const incidentSummary = incident?.title || incident?.content || '';

    if (!incidentSummary && monitorsDown.length === 0 && !error && !degradationDetailsUnavailable) {
      return statusText;
    }

    const visibleMonitors = monitorsDown.slice(0, TOOLTIP_SERVICE_LIMIT);
    const remainingMonitorCount = monitorsDown.length - visibleMonitors.length;

    return (
      <div className='status-tooltip-content'>
        <span className='status-tooltip-heading'>{statusText}</span>

        {statusErrorMessage && (
          <span className='status-tooltip-refresh-error'>
            {statusErrorMessage}
          </span>
        )}

        {degradationDetailsUnavailable && (
          <span className='status-tooltip-detail-fallback'>
            {t('description.statusDetailsUnavailable')}
          </span>
        )}

        {incidentSummary && (
          <div className='status-tooltip-section'>
            <span className='status-tooltip-label'>{incidentHeading}</span>
            <span>{incidentSummary}</span>
          </div>
        )}

        {visibleMonitors.length > 0 && (
          <div className='status-tooltip-section'>
            <span className='status-tooltip-label'>
              {t('title.affectedServices', { count: monitorsDown.length })}
            </span>
            <ul className='status-tooltip-services'>
              {visibleMonitors.map((monitor, index) => (
                <li className='status-tooltip-service' key={`${monitor.title}-${index}`}>
                  <span className='status-tooltip-service-name'>
                    <span className='status-tooltip-service-dot' aria-hidden='true' />
                    <span>{monitor.title}</span>
                  </span>
                  {monitor.content && (
                    <span className='status-tooltip-service-content'>{monitor.content}</span>
                  )}
                </li>
              ))}
              {remainingMonitorCount > 0 && (
                <li className='status-tooltip-more'>
                  {t('title.moreAffectedServices', { count: remainingMonitorCount })}
                </li>
              )}
            </ul>
          </div>
        )}
      </div>
    );
  }, [degradationDetailsUnavailable, error, incident, incidentHeading, monitorsDown, statusErrorMessage, statusText, t]);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <Tooltip
        title={isDesktop ? statusTooltipContent : null}
        placement='bottom'
        trigger='hover'
        classNames={{ root: 'service-status-tooltip' }}
      >
        <div
          className='status-indicator'
          onClick={showModal}
          onKeyDown={(event) => handleKeyboardAction(event, showModal)}
          role='button'
          tabIndex={0}
          aria-label={t('action.viewServiceStatus')}
          aria-description={statusAccessibleSummary}
        >
          {loading && !statusData ? (
            <Spin size='small' />
          ) : (
            <Badge status={status.badgeStatus} color={status.color} text={isDesktop ? statusText : null} />
          )
          }
        </div>
      </Tooltip>

      <Modal
        title={t('title.serviceStatus')}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={
          <Space orientation='vertical' className='w-100' >
            <Divider size='small' />
            <Space className='w-100 justifySpaceBetween' align='center' orientation={isDesktop ? 'horizontal' : 'vertical'}>
              <Space size={0} className='status-refresh' align='center'>
                <Text type='secondary' className='text-sm'>
                  {lastUpdated && t('description.statusChecked', { time: formatTime(lastUpdated) })}
                </Text>
                <Tooltip title={t('title.refresh')}>
                  <Button aria-label={t('action.refreshServiceStatus')} size='small' type='text' className='refresh-btn' loading={loading} icon={<ReloadOutlined />} onClick={fetchStatus} />
                </Tooltip>
              </Space>
              <Button
                size='small'
                type='link'
                href='https://status.scraper.systems/'
                target='_blank'
                rel='noopener noreferrer'
                icon={<OpenLink />}
                iconPlacement='end'
                className='open-status-page-btn'
              >
                {t('action.viewAllServiceStatuses')}
              </Button>
            </Space>
          </Space>
        }
        width={600}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 280px)',
            overflowY: 'auto',
            overflowX: 'hidden'
          }
        }}
        className='status-modal'
      >
        {loading && !statusData ? (
          <Space className='w-100 p-40 justifyCenter'>
            <Spin size='large'/>
          </Space>
        ) : statusData ? (
          <Space className='w-100 mt-12' orientation='vertical' size={16}>
            {error && (
              <Alert
                type='warning'
                title={t('description.statusRefreshFailure')}
                showIcon
              />
            )}
            <Alert
              type={status.alertType}
              title={statusText}
              description={degradationDetailsUnavailable
                ? t('description.statusDetailsUnavailable')
                : undefined}
              showIcon
            />
            {hasIncidentDetails && (
              <Space orientation='vertical' className='incident'>
                <Text strong>{incidentHeading}</Text>
                <Alert
                  title={incident.title || incidentHeading}
                  description={hasIncidentDescription ? (
                    <Space orientation='vertical'>
                      {incident.content && (
                        <Text style={{ whiteSpace: 'pre-line' }}>
                          {incident.content}
                        </Text>
                      )}
                      <Space orientation='vertical' size={0}>
                        {incident.createdDate && (
                          <Text type='secondary' className='text-sm'>{t('title.created')}: {formatDate(incident.createdDate)}</Text>
                        )}
                        {incident.lastUpdatedDate && (
                          <Text type='secondary' className='text-sm'>{t('title.lastUpdated')}: {formatDate(incident.lastUpdatedDate)}</Text>
                        )}
                      </Space>
                    </Space>
                  ) : undefined}
                  type={
                    incident.style === 'danger' ? 'error' :
                      incident.style === 'warning' ? 'warning' :
                        'info'
                  }
                  showIcon
                />
              </Space>
            )}
            {monitorsDown.length > 0 && (
              <Space className='w-100' orientation='vertical'>
                <Text strong>{t('title.affectedServices', { count: monitorsDown.length })}</Text>
                <Space className='w-100' orientation='vertical'>
                  {monitorsDown.map((monitor, index) =>
                    <Alert
                      key={`${monitor.title}-${index}`}
                      showIcon
                      title={
                        <Space className='w-100 justifySpaceBetween'>
                          <Text>{monitor.title}</Text>
                          <Text type='secondary'>{monitor.content}</Text>
                        </Space>
                      }
                      type='error'/>
                  )}
                </Space>
              </Space>
            )}
            <Space size={16} className='w-100' orientation='vertical'>
              <Divider size='small' />
              <a
                className='subscribe-card'
                href='https://t.me/DataServicesStatus'
                target='_blank'
                rel='noopener noreferrer'
              >
                <Space className='w-100 justifySpaceBetween' align='center'>
                  <Space orientation='vertical' size={0}>
                    <Space align='center' >
                      <TelegramLogo className='subscribe-icon' />
                      <Text strong>{t('title.getUpdatesInTelegram')}</Text>
                    </Space>
                    <Text type='secondary' className='text-sm'>{t('description.instantNotifications')}</Text>
                  </Space>
                  <Text className='join-text'>
                    {t('action.joinChannel')} <ArrowRightOutlined />
                  </Text>
                </Space>
              </a>
            </Space>
          </Space>
        ) : (
          <Space className='p-24 justifyCenter'>
            <Alert
              title={t('title.statusUnavailable')}
              description={t('description.statusLoadFailure')}
              type='error'
              showIcon
            />
          </Space>
        )}
      </Modal>
    </>
  );
};

export default StatusIndicator;
